import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { baseURL } from '../Constants';
import { Author } from '../types';

const newsUrl = baseURL + "/NewsArticle";

// Define article type matching the C# model
export interface Article {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  imageUrl: string;
  date: string;
  category: string;
  tags: string[];
  authorSteamId?: string;
  author?: Author;
}

// Define context type
interface NewsContextType {
  articles: Article[];
  isLoading: boolean;
  error: string | null;
  getLatestArticles: (count?: number) => Promise<Article[]>;
  getLatestNewsArticles: (count?: number) => Promise<Article[]>;
  getArticlesByTag: (tag: string) => Promise<Article[]>;
  getNewsArticlesByTag: (tag: string) => Promise<Article[]>;
  getArticleById: (id: string) => Promise<Article | null>;
  refreshArticles: () => Promise<void>;
}

// Create context
const NewsContext = createContext<NewsContextType | null>(null);

export const NewsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to filter out info content
  const isInfoContent = (article: Article): boolean => {
    return article.tags.includes('info') || article.category === 'Information';
  };

  // Fetch all articles from the backend API
  const refreshArticles = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await axios.get(newsUrl);
      setArticles(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching articles:', err);
      setError('Failed to load articles. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Get latest articles (includes info content)
  const getLatestArticles = async (count: number = 3): Promise<Article[]> => {
    try {
      const response = await axios.get(`${newsUrl}/latest/${count}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching latest articles:', err);
      // Fall back to client-side filtering if the API fails
      return [...articles]
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, count);
    }
  };

  // Get latest news articles (excludes info content)
  const getLatestNewsArticles = async (count: number = 3): Promise<Article[]> => {
    try {
      // Fetch more articles to ensure we have enough after filtering
      const response = await axios.get(`${newsUrl}/latest/${count * 3}`); 
      const filtered = response.data.filter((article: Article) => !isInfoContent(article));
      return filtered.slice(0, count);
    } catch (err) {
      console.error('Error fetching latest news articles:', err);
      // Fall back to client-side filtering if the API fails
      return [...articles]
        .filter(article => !isInfoContent(article))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, count);
    }
  };

  // Get articles by tag (includes info content)
  const getArticlesByTag = async (tag: string): Promise<Article[]> => {
    if (tag === 'all') {
      return articles;
    }
    
    try {
      const response = await axios.get(`${newsUrl}/tag/${encodeURIComponent(tag)}`);
      return response.data;
    } catch (err) {
      console.error(`Error fetching articles by tag ${tag}:`, err);
      // Fall back to client-side filtering if the API fails
      return articles.filter(article => 
        article.tags.includes(tag)
      );
    }
  };

  // Get news articles by tag (excludes info content)
  const getNewsArticlesByTag = async (tag: string): Promise<Article[]> => {
    if (tag === 'all') {
      return articles.filter(article => !isInfoContent(article));
    }

    // Use client-side filtering for category and tags
    // This is more reliable than relying on API endpoints that may not exist
    return articles.filter(article =>
      (article.category === tag || article.tags.includes(tag)) && !isInfoContent(article)
    );
  };

  // Get article by ID
  const getArticleById = async (id: string): Promise<Article | null> => {
    try {
      const response = await axios.get(`${newsUrl}/${id}`);
      return response.data;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        return null;
      }
      console.error(`Error fetching article ${id}:`, err);
      // Fall back to client-side lookup if the API fails
      return articles.find(a => a.id === id) || null;
    }
  };

  // Load articles on initial render
  useEffect(() => {
    refreshArticles();
  }, []);

  // Return context provider
  return (
    <NewsContext.Provider value={{
      articles,
      isLoading,
      error,
      getLatestArticles,
      getLatestNewsArticles,
      getArticlesByTag,
      getNewsArticlesByTag,
      getArticleById,
      refreshArticles
    }}>
      {children}
    </NewsContext.Provider>
  );
};

// Custom hook for using the news context
export const useNews = (): NewsContextType => {
  const context = useContext(NewsContext);
  if (context === null) {
    throw new Error('useNews must be used within a NewsProvider');
  }
  return context;
};